"use client";

import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from "react";
import api from "@/lib/axiosInstance";

interface User {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    isActive: boolean;
}

interface AuthContextType {
    user: User | null;
    loading: boolean;
    login: (email: string, password: string) => Promise<void>;
    register: (firstName: string, lastName: string, email: string, password: string, phone: string) => Promise<void>;
    logout: () => Promise<void>;
    revokeAllSessions: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const REFRESH_INTERVAL = 14 * 60 * 1000; // 14 minutes

export function AuthProvider({ children }: { children: ReactNode }) {
    
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const initializationAttempted = useRef(false);

    // Fetch user profile
    const fetchUserProfile = useCallback(async () => {
        try {
            const response = await api.get("/api/auth/me");
            console.log("Fetch user response:", response);
            setUser(response.data);
            return response.data;
        } catch (error: any) {
            if (error.response?.status === 401) {
                setUser(null);
            }
            throw error;
        }
    }, []);

    // Refresh access token
    const refreshAccessToken = useCallback(async (): Promise<boolean> => {
        try {
            const response = await api.post("/api/auth/refresh");
            
            if (response.data.user) {
                setUser(response.data.user);
                return true;
            }
            
            return false;
        } catch (error: any) {
            if (error.response?.status === 401) {
                setUser(null);
            }
            return false;
        }
    }, []);

    // Schedule token refresh
    const scheduleTokenRefresh = useCallback(() => {
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
        }

        refreshTimeoutRef.current = setTimeout(async () => {
            const success = await refreshAccessToken();
            
            if (success && user) {
                scheduleTokenRefresh();
            }
        }, REFRESH_INTERVAL);
    }, [refreshAccessToken, user]);

    // Initialize auth on mount
    useEffect(() => {
        if (initializationAttempted.current) return;
        
        initializationAttempted.current = true;

        const initAuth = async () => {
            try {
                // Skip session restore on auth pages
                if (typeof window !== 'undefined') {
                    const path = window.location.pathname;
                    const isAuthPage = path === '/login' || path === '/signup' || path === '/register';
                    
                    if (isAuthPage) {
                        setLoading(false);
                        return;
                    }
                }
                
                await fetchUserProfile();
            } catch (error) {
                // No active session
            } finally {
                setLoading(false);
            }
        };

        initAuth();

        return () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
        };
    }, [fetchUserProfile]);

    // Start/stop refresh schedule based on user state
    useEffect(() => {
        if (user) {
            scheduleTokenRefresh();
        } else {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
                refreshTimeoutRef.current = null;
            }
        }

        return () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
        };
    }, [user, scheduleTokenRefresh]);

    // Register - server handles everything
    const register = async (
        firstName: string, 
        lastName: string, 
        email: string, 
        password: string,
        phone: string
    ): Promise<void> => {
        // Server auto-logs in after registration
        const response = await api.post("/api/auth/register", {
            firstName,
            lastName,
            email,
            password,
            phone
        });

        setUser(response.data.user);
        
        // Dispatch event for other contexts to refresh
        window.dispatchEvent(new CustomEvent('userLogin', {
            detail: { timestamp: Date.now() }
        }));
    };

    // Login - server handles cart/wishlist merge
    const login = async (email: string, password: string): Promise<void> => {
        const response = await api.post('/api/auth/login', { email, password });
        
        setUser(response.data.user);
        
        // Dispatch event for other contexts to refresh
        window.dispatchEvent(new CustomEvent('userLogin', {
            detail: { timestamp: Date.now() }
        }));
    };

    // Logout
    const logout = async () => {
        try {
            await api.post("/api/auth/logout");
            setUser(null);

            // Dispatch event for cleanup
            window.dispatchEvent(new CustomEvent('userLogout', {
                detail: { timestamp: Date.now() }
            }));

            // Smart redirect
            if (typeof window !== 'undefined') {
                const currentPath = window.location.pathname;
                const protectedPages = ['/account', '/profile', '/orders', '/checkout'];
                const isProtectedPage = protectedPages.some(page => currentPath.startsWith(page));
                
                if (isProtectedPage) {
                    window.location.href = '/';
                } else {
                    window.location.reload();
                }
            }
        } catch (error: any) {
            console.error("Logout error:", error);
            setUser(null);
            
            window.dispatchEvent(new CustomEvent('userLogout', {
                detail: { timestamp: Date.now() }
            }));
            
            if (typeof window !== 'undefined') {
                window.location.reload();
            }
        } 
    };

    // Revoke all sessions
    const revokeAllSessions = async () => {
        await api.post("/api/auth/revoke-all-sessions");
        await logout();
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                login,
                register,
                logout,
                revokeAllSessions
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}